<template>
  <div class="container mt-5">
     <!-- Loading Spinner -->
     <div v-if="loading" class="loading-spinner-container">
      <div class="loading-spinner"></div>
    </div>
    <div v-if="teamScores.length > 0">
      <div class="custom-card p-4 rounded shadow-sm mb-4">
        <div class="team-info mb-4">
          <h3 class="mb-1 text-center">{{ teamName }}</h3>
          <p class="mb-3 text-center npmtext-muted">Team ID: {{ teamId }}</p>
          <div class="row text-center">
            <div class="col-md-4">
              <h5>Total Score</h5>
              <p class="display-4">{{ totalScore + bonusPoints }}</p>
            </div>
            <div class="col-md-4">
              <h5>Regular Points</h5>
              <p class="display-6">{{ totalScore }}</p>
            </div>
            <div class="col-md-4">
              <h5>
                Bonus Points
                <button type="button" class="btn btn-link p-0" @click="showBonusInfo = true" @touchend="showBonusInfo = true">
                  <i class="fa-solid fa-circle-info info-icon"></i>
                </button>
              </h5>
              <p class="display-6">{{ bonusPoints }}</p>
            </div>
          </div>
        </div>
        <hr>
        <div class="table-responsive">
          <table class="table table-bordered table-hover custom-table">
            <thead class="thead-custom">
              <tr>
                <th>Round</th>
                <th>Score</th>
                <th>Our Answers</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(round, index) in filteredScores" :key="index">
                <td>{{ round.roundName }}</td>
                <td>{{ round.roundScore }}</td>
                <td>
                  <ul class="list-unstyled">
                    <li v-for="(answer, answerIndex) in round.answers" :key="answerIndex">
                      {{ answer }}
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else>
      <h3 class="mb-1 text-center">{{ teamName }}</h3>
          <p class="mb-3 text-center npmtext-muted">Team ID: {{ teamId }}</p>
      <p>No scores available yet.</p>
    </div>

    <!-- Bonus Points Modal -->
    <div v-if="showBonusInfo" class="modal fade show" tabindex="-1" style="display: block;" @click.self="closeBonusInfo">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Bonus Points Information</h5>
            <button type="button" class="btn-close" @click="closeBonusInfo"></button>
          </div>
          <div class="modal-body">
            <p>Get 50 bonus points by making a post on Facebook or Instagram, tagging your venue and @SyracuseSays, and showing your host.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeBonusInfo">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { db } from '@/firebase';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';

export default {
  name: 'OurScoresPage',
  setup() {
    const teamId = ref(localStorage.getItem('teamId') || '');
    const teamName = ref('');
    const teamScores = ref([]);
    const totalScore = ref(0);
    const bonusPoints = ref(0);
    const showBonusInfo = ref(false);
    const loading = ref(true);

    const filteredScores = computed(() => {
      return teamScores.value.filter(round => round.roundName !== 'Bonus Points Survey');
    });

    const fetchTeamData = async () => {
  if (teamId.value) {
    const teamDocRef = doc(db, 'teams', teamId.value);
    const teamDocSnap = await getDoc(teamDocRef);

    if (teamDocSnap.exists()) {
      teamName.value = teamDocSnap.data().teamName;

      const submissionsQuery = query(
        collection(db, `games/${teamDocSnap.data().gameId}/submissions`),
        where('teamId', '==', teamId.value)
      );

      const submissionsSnap = await getDocs(submissionsQuery);

      if (!submissionsSnap.empty) {
        const scores = [];
        let total = 0;
        let bonusTotal = 0;

        for (const docSnap of submissionsSnap.docs) {
          const submission = docSnap.data();

          // Safely convert to number and handle non-numeric values
          const submissionScore = parseFloat(submission.totalScore) || 0;

          const roundDocRef = doc(db, `games/${teamDocSnap.data().gameId}/rounds/${submission.roundId}`);
          const roundDocSnap = await getDoc(roundDocRef);
          const roundNumber = roundDocSnap.exists() ? roundDocSnap.data().roundNumber : submission.roundId;

          if (submission.isBonus) {
            bonusTotal += submissionScore;
            bonusPoints.value = bonusTotal;
          } else {
            total += submissionScore;

            scores.push({
              roundNumber: roundNumber,
              roundName: submission.roundName,
              roundScore: submissionScore,
              answers: submission.answers,
            });
          }
        }

        scores.sort((a, b) => a.roundNumber - b.roundNumber);

        teamScores.value = scores;
        totalScore.value = total;
      }
    }
  }
  loading.value = false;
};

    onMounted(() => {
      fetchTeamData();
    });

    return {
      teamId,
      teamName,
      teamScores,
      totalScore,
      bonusPoints,
      showBonusInfo,
      loading,
      filteredScores,  // Returning the computed property
    };
  },
  methods: {
    closeBonusInfo() {
      this.showBonusInfo = false;
    },
  },
};
</script>

<style scoped>
.custom-card {
  background-color: #f8f9fa; /* Soft light gray background */
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Team info section */
.team-info h3 {
  font-weight: bold;
}

.team-info .text-muted {
  font-size: 0.9rem;
}

.table-responsive {
  margin-top: 20px;
}

/* Table styles */
.custom-table {
  background-color: #ffffff; /* White background for the table */
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

th, td {
  padding: 12px;
  text-align: center;
}

.thead-custom th {
  background-color: #003366; /* Dark blue for the header */
  color: #fff;
  font-weight: bold;
}

.table-hover tbody tr:hover {
  background-color: #ffebcd; /* Light beige on hover */
}

.table-bordered {
  border: 1px solid #ddd;
}

td {
  border-color: #ddd; /* Lighter border for a softer look */
}

.info-icon {
  color: #003366; /* Dark blue */
}

.modal {
  background: rgba(0, 0, 0, 0.5); /* Darken background when modal is open */
}

.modal-content {
  border-radius: 10px;
}
  .loading-spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  .loading-spinner {
    border: 8px solid #f3f3f3; /* Light grey */
    border-top: 8px solid #003366; /* Blue */
    border-radius: 50%;
    width: 60px;
    height: 60px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
